import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useMutation, useQuery } from "convex/react";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components/ui/table";
import CreateFirmDialog from "./CreateFirmDialog";

const STAFF_TENANT_SLUG = "clerical-com";

type FirmRow = {
	tenantId: Id<"tenants">;
	name: string;
	slug: string;
	memberCount: number;
	pendingUserCount: number;
	codexStatus?: "pending" | "in_progress" | "complete";
};

export default function FirmsPage() {
	const firms = useQuery(api.staff.listAllFirms);
	const deleteFirm = useMutation(api.staff.deleteFirm);
	const [createOpen, setCreateOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<FirmRow | null>(null);
	const [deleteError, setDeleteError] = useState<string | null>(null);

	const { mutate: runDelete, isPending: deleting } = useReactQueryMutation({
		mutationFn: async (tenantId: Id<"tenants">) => {
			return await deleteFirm({ tenantId });
		},
		onSuccess: () => {
			setDeleteTarget(null);
			setDeleteError(null);
		},
		onError: (err) => {
			setDeleteError(err instanceof Error ? err.message : String(err));
		},
	});

	function openDelete(firm: FirmRow) {
		setDeleteError(null);
		setDeleteTarget(firm);
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-end">
				<Button onClick={() => setCreateOpen(true)}>
					<PlusIcon data-icon="inline-start" />
					Create firm
				</Button>
			</div>

			<div className="rounded-lg border border-border bg-background">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Name</TableHead>
							<TableHead>Slug</TableHead>
							<TableHead className="text-right">Members</TableHead>
							<TableHead className="text-right">Pending</TableHead>
							<TableHead>Codex</TableHead>
							<TableHead />
						</TableRow>
					</TableHeader>
					<TableBody>
						{firms === undefined ? (
							<TableRow>
								<TableCell colSpan={6} className="text-muted-foreground">
									Loading firms...
								</TableCell>
							</TableRow>
						) : firms.length === 0 ? (
							<TableRow>
								<TableCell colSpan={6} className="text-muted-foreground">
									No firms yet. Click "Create firm" to add one.
								</TableCell>
							</TableRow>
						) : (
							firms.map((firm) => {
								const isStaffFirm = firm.slug === STAFF_TENANT_SLUG;
								return (
									<TableRow key={firm.tenantId}>
										<TableCell>
											<Link
												to={`/staff/firms/${firm.tenantId}`}
												className="font-medium text-foreground hover:underline"
											>
												{firm.name}
											</Link>
										</TableCell>
										<TableCell className="text-muted-foreground">
											{firm.slug}
										</TableCell>
										<TableCell className="text-right">
											{firm.memberCount}
										</TableCell>
										<TableCell className="text-right">
											{firm.pendingUserCount}
										</TableCell>
										<TableCell className="capitalize text-muted-foreground">
											{firm.codexStatus?.replace("_", " ") ?? "—"}
										</TableCell>
										<TableCell className="text-right">
											<Button
												variant="ghost"
												size="sm"
												disabled={isStaffFirm}
												title={
													isStaffFirm
														? "The Clerical staff firm can't be deleted"
														: "Delete firm"
												}
												onClick={() => openDelete(firm)}
											>
												<Trash2Icon />
												<span className="sr-only">Delete</span>
											</Button>
										</TableCell>
									</TableRow>
								);
							})
						)}
					</TableBody>
				</Table>
			</div>

			<CreateFirmDialog open={createOpen} onOpenChange={setCreateOpen} />

			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(open) => {
					if (!open && !deleting) {
						setDeleteTarget(null);
						setDeleteError(null);
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete firm?</AlertDialogTitle>
						<AlertDialogDescription>
							{deleteTarget ? (
								<>
									This permanently removes <strong>{deleteTarget.name}</strong>,
									its {deleteTarget.memberCount} member
									{deleteTarget.memberCount === 1 ? "" : "s"}, and{" "}
									{deleteTarget.pendingUserCount} pending invite
									{deleteTarget.pendingUserCount === 1 ? "" : "s"}. Members who
									aren't in any other firm will also be fully deleted (user,
									sessions, and linked accounts). This cannot be undone.
								</>
							) : null}
						</AlertDialogDescription>
					</AlertDialogHeader>
					{deleteError ? (
						<p className="text-sm text-destructive">{deleteError}</p>
					) : null}
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							disabled={deleting}
							onClick={(e) => {
								e.preventDefault();
								if (!deleteTarget) return;
								runDelete(deleteTarget.tenantId);
							}}
						>
							{deleting ? "Deleting..." : "Delete firm"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
